import { useMutation, useQuery, useQueryClient } from 'react-query';
import { useTranslation } from 'react-i18next';

import { KEYS } from 'helpers/react-query.config';
import { useApiContext } from 'context/ApiContext';
import { useGlobalContext } from 'context/GlobalContext';
import { AxiosServiceError } from 'interfaces/general';

type Props = {
  clientId: number;
  enabled: boolean;
};

function useReportConfig({ clientId, enabled }: Props) {
  const { showApiError, showSuccess } = useGlobalContext();
  const { ticket: ticketApi } = useApiContext();
  const { t } = useTranslation('global');
  const queryClient = useQueryClient();

  const { data: config, isLoading } = useQuery(
    [KEYS.ticket.tickets.getTicketReportConfigs(clientId), clientId],
    () => ticketApi.tickets.getTicketReportConfigs({ clientId }),
    {
      enabled,
      refetchOnWindowFocus: false,
      select: ({ data }) => data?.data,
      onError: (error) =>
        showApiError(error, t('common.commonMessages.genericRequestError'))
    }
  );

  const saveReportConfig = useMutation(
    (data: { clientId: number; reportConfig: string }) =>
      ticketApi.tickets.saveTicketReportConfig(data),
    {
      onError: (error: AxiosServiceError) => {
        showApiError(error, t('common.commonMessages.genericRequestError'));
      },
      onSuccess: async () => {
        showSuccess(t('ticket.ticketDetail.reportConfigSuccess'));
        await queryClient.refetchQueries(
          KEYS.ticket.tickets.getTicketReportConfigs(clientId)
        );
      }
    }
  );

  return {
    reportConfig: { data: config, isLoading },
    saveReportConfig
  };
}

export default useReportConfig;
